import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import TextInput from '@xingternal/malt-text-input';
import Textlink from '@xingternal/malt-textlink';
import { PrimaryButtonMedium as PrimaryButton } from '@xingternal/malt-button';
import { Checkbox } from '@xingternal/malt-checkbox';
import { testable } from '@xingternal/malt-util';
import { QA_MALT } from '@xingternal/malt-constants';

import StretchButtonGroup from './../../Components/StretchButtonGroup/StretchButtonGroup';
import PasswordField from './../../Components/PasswordField/PasswordField';
import FormMessage from './../../Components/FormMessage/FormMessage';
import LockImage from './../../Components/Illustrations/LockImage';
import { CustomH } from './../../Components/CustomMalt/Typography';
import CustomTextButton from './../../Components/CustomMalt/TextButton';

import styles from './LoginUser.css';
import messages from './messages';

const partnerLink = (name, website) => (
  <Textlink href={website}>{name}</Textlink>
);

const partnerNotice = authPartner =>
  authPartner && (
    <FormattedMessage
      {...messages.partnerAuthIntroduction}
      values={{
        application: partnerLink(authPartner.name, authPartner.website),
      }}
    />
  );

const TestableForm = testable(QA_MALT, 'form');

const LoginUser = ({
  handleSubmit,
  handleChange,
  formMessage: { error, notice },
  setErrors,
  errors,
  isSubmitting,
  authPartner,
  values: { permanent, username, password },
  usernameRef,
  passwordRef,
  didResetPassword,
  signupUrl,
}) => {
  useEffect(() => {
    if (error) {
      setErrors({ credentials: error });
    }
  }, []);

  return (
    <TestableForm onSubmit={handleSubmit} testId={'login-form'} method={'POST'}>
      <LockImage />

      <CustomH>
        {didResetPassword ? (
          <FormattedMessage {...messages.postRecoveryTitle} />
        ) : (
          <FormattedMessage {...messages.title} />
        )}
      </CustomH>

      <FormMessage notice={partnerNotice(authPartner)} />

      <TextInput
        name={'username'}
        value={username}
        onChange={handleChange}
        inputRef={usernameRef}
        label={<FormattedMessage {...messages.emailLabel} />}
        type={'text'}
        size={'m'}
        required
        autoFocus
      />

      <PasswordField
        label={<FormattedMessage {...messages.passwordLabel} />}
        handleChange={handleChange}
        inputRef={passwordRef}
        value={password}
      />

      <div className={styles.inputActions}>
        <div className={styles.rememberMeCheckbox}>
          <Checkbox
            checked={permanent}
            name={'permanent'}
            onChange={handleChange}
          >
            <FormattedMessage {...messages.rememberMe} />
          </Checkbox>
        </div>

        <CustomTextButton
          isPetrol
          to={'/recovery'}
          data-qa={'malt-login-problems-link'}
        >
          <FormattedMessage {...messages.problemsLoginInLabel} />
        </CustomTextButton>
      </div>

      {errors.general ? (
        <FormMessage
          error={<FormattedMessage {...messages.loginRequestError} />}
        />
      ) : (
        <FormMessage notice={notice} error={errors.credentials} />
      )}

      <StretchButtonGroup>
        <PrimaryButton
          data-qa={'malt-login-submit'}
          type={'submit'}
          loading={isSubmitting}
        >
          <FormattedMessage {...messages.submitLabel} />
        </PrimaryButton>

        <CustomTextButton
          isPetrol
          href={signupUrl}
          className={styles.registerNow}
        >
          <FormattedMessage {...messages.registerNow} />
        </CustomTextButton>
      </StretchButtonGroup>
    </TestableForm>
  );
};

LoginUser.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  usernameRef: PropTypes.object,
  passwordRef: PropTypes.object,
  errors: PropTypes.object.isRequired,
  formMessage: PropTypes.object,
  values: PropTypes.shape({
    permanent: PropTypes.bool,
    username: PropTypes.string,
    password: PropTypes.string,
  }),
  isSubmitting: PropTypes.bool,
  authPartner: PropTypes.shape({
    name: PropTypes.string,
    website: PropTypes.string,
  }),
  didResetPassword: PropTypes.bool,
  setErrors: PropTypes.func.isRequired,
  signupUrl: PropTypes.string,
};

export default LoginUser;
